// Your days: when each tradition hands you back your own birth moment.
// Deterministic (astronomy-engine and the app's own calculators).
//
//   maya-sign   your day sign comes round again, every 20 days (civil date)
//   tzolkin     the same sign and number, the full Tzolk'in birthday, every 260 days
//   chinese     a day whose branch is your year animal (dayPillar), every 12 days
//   janma       the sidereal (Lahiri) Moon back in your birth nakshatra, about every 27.3 days
//   lunar       the tropical Moon back on its natal degree (the lunar return), to the minute
//   solar       the Sun back on its natal degree (the solar return), to the minute
//
// Day counts (Maya, Chinese) are civil dates in the reader's time zone, the
// way the daily lenses read "today". Moon and Sun returns are instants.
// Without a birth time the lunar and solar returns are skipped (the Moon
// moves about 13° and the Sun about 1° over a birth day, so neither degree is
// known), and janma tara is skipped when the Moon changed nakshatra that day.
// The Chinese day turns at midnight, as in the printed almanacs and
// dayPillar (some BaZi schools turn it at 23:00, the start of the Zi hour).

import { bodyLongitude, localToUtc, signedDelta } from "./core.js";
import { calculateMayanChart } from "./mayan.js";
import { calculateChineseChart } from "./chinese.js";
import { computeNatal } from "./natal.js";
import { nextSolarReturn } from "./transits.js";
import { NAKSHATRAS, birthNakshatras, civilDate, dayPillar, nakshatraIndexAt } from "./dailyLenses.js";

const DAY_MS = 86400000;
const HOUR_MS = 3600000;

export const RETURN_KINDS = {
  "maya-sign": { tradition: "mayan", every: "every 20 days" },
  tzolkin: { tradition: "mayan", every: "every 260 days" },
  chinese: { tradition: "chinese", every: "every 12 days" },
  janma: { tradition: "vedic", every: "about every 27 days" },
  lunar: { tradition: "western", every: "about every 27 days" },
  solar: { tradition: "western", every: "once a year" },
};

/* ── civil-date helpers (whole UTC day numbers, so DST never shifts a day) ── */

const dayNumber = ({ year, month, day }) => Math.floor(Date.UTC(year, month - 1, day) / DAY_MS);
const fromDayNumber = (n) => {
  const d = new Date(n * DAY_MS);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
};
const mod = (n, m) => ((n % m) + m) % m;
const startOfDay = (civil, tz) => localToUtc({ ...civil, hour: 0, minute: 0 }, tz);

/** Next civil days (today included) that are `period` days on from `anchor`. */
function cycleDays(anchor, today, period, count) {
  const first = dayNumber(today) + mod(dayNumber(anchor) - dayNumber(today), period);
  return Array.from({ length: count }, (_, i) => fromDayNumber(first + i * period));
}

/* ── instant searches ── */

/** Next instant after `from` that the Moon's tropical longitude reaches `target`. */
export function nextLunarReturn(target, from) {
  // The Moon never goes retrograde: step 6h until it passes the target, then bisect.
  let lo = from.getTime();
  const ahead = (t) => signedDelta(bodyLongitude("moon", new Date(t)), target) > 0; // still short of it
  let hi = lo;
  for (let i = 0; i < 130; i++) {
    hi = lo + 6 * HOUR_MS;
    if (!ahead(hi) && ahead(lo)) break;
    lo = hi;
  }
  while (hi - lo > 1000) {
    const mid = (lo + hi) / 2;
    if (ahead(mid)) lo = mid; else hi = mid;
  }
  return new Date(hi);
}

/** Bisect a nakshatra boundary between lo (inside == a) and hi (inside == b). */
function boundary(lo, hi, idx, inside) {
  while (hi - lo > 1000) {
    const mid = (lo + hi) / 2;
    if ((nakshatraIndexAt(new Date(mid)) === idx) === inside) lo = mid; else hi = mid;
  }
  return hi;
}

/**
 * The Moon's stays in nakshatra `idx`, starting with the one under way at
 * `from` (if any). Each is { start, end } to the second.
 */
export function janmaWindows(idx, from, count = 2) {
  const out = [];
  let t = from.getTime();
  if (nakshatraIndexAt(from) === idx) {
    // Under way: walk back to the entry, forward to the exit.
    let back = t;
    while (nakshatraIndexAt(new Date(back - HOUR_MS)) === idx) back -= HOUR_MS;
    const start = boundary(back - HOUR_MS, back, idx, false);
    let fwd = t;
    while (nakshatraIndexAt(new Date(fwd + HOUR_MS)) === idx) fwd += HOUR_MS;
    const end = boundary(fwd, fwd + HOUR_MS, idx, true);
    out.push({ start: new Date(start), end: new Date(end) });
    t = end + HOUR_MS;
  }
  while (out.length < count) {
    // Step hourly until the Moon is in the nakshatra (it takes about a day to cross one).
    let lo = t;
    for (let i = 0; i < 24 * 30 && nakshatraIndexAt(new Date(lo + HOUR_MS)) !== idx; i++) lo += HOUR_MS;
    const start = boundary(lo, lo + HOUR_MS, idx, false);
    let fwd = start;
    while (nakshatraIndexAt(new Date(fwd + HOUR_MS)) === idx) fwd += HOUR_MS;
    const end = boundary(fwd, fwd + HOUR_MS, idx, true);
    out.push({ start: new Date(start), end: new Date(end) });
    t = end + HOUR_MS;
  }
  return out;
}

/* ── the list ── */

/**
 * Every return kind's upcoming dates for a chart.
 * @param birthData the app's birthData shape
 * @param opts.date the moment to count from (default now)
 * @param opts.timeZone the reader's IANA zone, which sets "today" (default the device's)
 * @param opts.count occurrences per kind (default 2)
 * @returns { today, timeZone, kinds: { [kind]: { ...facts, next: [{ day, at?, end?, ongoing? }] } }, skipped: [{ kind, why }] }
 *   `day` is the civil date in the reader's zone; `at`/`end` are instants for
 *   the Moon and Sun returns.
 */
export function returnDays(birthData, { date = new Date(), timeZone, count = 2 } = {}) {
  if (!birthData?.birthDate?.year) return null;
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  let tz = timeZone || device;
  let today;
  try { today = civilDate(date, tz); } catch (e) { tz = device; today = civilDate(date, tz); }
  const timed = !!(birthData.hasBirthTime && birthData.birthTime && birthData.birthLocation?.timezone);
  const kinds = {};
  const skipped = [];
  const asDay = (at) => civilDate(at, tz);

  // Maya: the Tzolk'in day of the birth date (GMT correlation), counted forward.
  const maya = calculateMayanChart(birthData);
  kinds["maya-sign"] = { sign: maya.daySign, tone: maya.tone,
    next: cycleDays(birthData.birthDate, today, 20, count).map((day) => ({ day })) };
  kinds.tzolkin = { sign: maya.daySign, tone: maya.tone,
    next: cycleDays(birthData.birthDate, today, 260, count).map((day) => ({ day })) };

  // Chinese: the year animal as the Chart counts it (from Lunar New Year),
  // matched against each day's branch.
  const chinese = calculateChineseChart(birthData);
  const branch = chinese.animal.order - 1;
  const firstBranchDay = dayNumber(today) + mod(branch - dayPillar(today).branch, 12);
  kinds.chinese = { animal: chinese.animal,
    next: Array.from({ length: count }, (_, i) => {
      const day = fromDayNumber(firstBranchDay + 12 * i);
      return { day, pillar: dayPillar(day) };
    }) };

  // Vedic: janma tara, the Moon in the birth nakshatra.
  // Same test as the returns below: a time flag with no time is untimed.
  const naks = birthNakshatras(birthData.hasBirthTime && !birthData.birthTime ? { ...birthData, hasBirthTime: false } : birthData);
  if (naks.length === 1) {
    const idx = naks[0];
    kinds.janma = { nakshatra: NAKSHATRAS[idx], index: idx,
      // One already under way is listed today, with the day it ends.
      next: janmaWindows(idx, date, count).map((w) => {
        const ongoing = w.start <= date;
        return { day: ongoing ? today : asDay(w.start), at: w.start, end: w.end, endDay: asDay(w.end), ongoing };
      }) };
  } else {
    skipped.push({ kind: "janma", needs: "time", why: "The Moon changed nakshatra on the day you were born, so your birth star needs your birth time." });
  }

  // Western: lunar and solar returns, to the minute, only with a birth time.
  if (timed) {
    const natal = computeNatal(birthData);
    const moonLon = natal.points.moon.lon;
    const lunar = [];
    let from = date;
    for (let i = 0; i < count; i++) {
      const at = nextLunarReturn(moonLon, from);
      lunar.push({ day: asDay(at), at });
      from = new Date(at.getTime() + DAY_MS);
    }
    kinds.lunar = { lon: moonLon, sign: natal.points.moon.sign, next: lunar };
    const solar = [];
    from = date;
    for (let i = 0; i < count; i++) {
      const at = nextSolarReturn(natal, from);
      solar.push({ day: asDay(at), at });
      from = new Date(at.getTime() + 30 * DAY_MS);
    }
    kinds.solar = { lon: natal.points.sun.lon, sign: natal.points.sun.sign, next: solar };
  } else {
    // A time without a zone can't be placed either.
    const needs = birthData.hasBirthTime && birthData.birthTime ? "place" : "time";
    skipped.push({ kind: "lunar", needs, why: `Your lunar return needs your birth ${needs}.` });
    skipped.push({ kind: "solar", needs, why: `Your solar return to the minute needs your birth ${needs}.` });
  }

  return { today, timeZone: tz, kinds, skipped };
}

/**
 * The next `count` return days, soonest first: the next one of each kind (an
 * untimed chart has fewer kinds, so fewer rows; repeats of the same short
 * cycle would crowd out the point). Returns that land on the same
 * civil day are grouped. Each row: { day, sortAt, items: [{ kind, ...occurrence, ...facts }] }.
 */
export function nextReturnDays(birthData, { date = new Date(), timeZone, count = 5 } = {}) {
  // Only the next of each kind is listed (two, for the Tzolk'in birthday fold).
  const r = returnDays(birthData, { date, timeZone, count: 2 });
  if (!r) return null;
  const all = [];
  for (const [kind, k] of Object.entries(r.kinds)) {
    const { next, ...facts } = k;
    next.forEach((occ, i) => all.push({ kind, rank: i, ...facts, ...occ,
      sortAt: occ.at && !occ.ongoing ? occ.at : startOfDay(occ.day, r.timeZone) }));
  }
  // On the Tzolk'in birthday the day sign's own return is the same day; keep one.
  const birthdays = new Set(all.filter((o) => o.kind === "tzolkin").map((o) => dayNumber(o.day)));
  const kept = all.filter((o) => !(o.kind === "maya-sign" && birthdays.has(dayNumber(o.day))));
  kept.sort((a, b) => a.sortAt - b.sortAt);
  const firsts = kept.filter((o) => o.rank === 0 || (o.kind === "maya-sign" && o.rank === 1 && !kept.some((k) => k.kind === "maya-sign" && k.rank === 0)));
  const picked = firsts.slice(0, count);
  // Group by civil day.
  const rows = new Map();
  picked.sort((a, b) => a.sortAt - b.sortAt).forEach((o) => {
    const key = dayNumber(o.day);
    if (!rows.has(key)) rows.set(key, { day: o.day, sortAt: o.sortAt, items: [] });
    rows.get(key).items.push(o);
  });
  return { today: r.today, timeZone: r.timeZone, rows: [...rows.values()], skipped: r.skipped };
}

/** Whole days from `today` to `day` (both civil). */
export const daysUntil = (today, day) => dayNumber(day) - dayNumber(today);

// Biggest first, when two land on one day.
const TODAY_ORDER = ["solar", "tzolkin", "janma", "lunar", "maya-sign", "chinese"];

/**
 * The returns that fall on the reader's calendar day (dy-002, Today's card).
 * Counted from the day's midnight, so a janma tara that ended this morning,
 * or a lunar return at 3 AM, still counts for the day. On the Tzolk'in
 * birthday the day sign's own return folds into it.
 * @returns { today, timeZone, items: [{ kind, ...facts, day, at?, end? }] } or null
 *   Maya items also carry `daySignIdx` (0 = Imox) and `todayTone`; janma items
 *   carry `noteDay`, the date the visit began (one note per visit).
 */
export function returnsToday(birthData, { date = new Date(), timeZone } = {}) {
  if (!birthData?.birthDate?.year) return null;
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  let tz = timeZone || device;
  let today;
  try { today = civilDate(date, tz); } catch (e) { tz = device; today = civilDate(date, tz); }
  const midnight = startOfDay(today, tz);
  const r = returnDays(birthData, { date: midnight, timeZone: tz, count: 1 });
  if (!r) return null;
  const maya = calculateMayanChart(birthData);
  const todayTone = calculateMayanChart({ birthDate: today }).tone.number;
  const items = [];
  for (const [kind, k] of Object.entries(r.kinds)) {
    const { next, ...facts } = k;
    const occ = next[0];
    if (!occ || dayNumber(occ.day) !== dayNumber(today)) continue;
    // A janma tara visit lasts about a day and usually spans two dates; it
    // counts on each date it covers for at least six hours (so on one or two,
    // never on a date it only grazes), and its note belongs to the visit.
    let extra = {};
    if (kind === "janma") {
      const dayEnd = startOfDay(fromDayNumber(dayNumber(today) + 1), tz);
      const covered = Math.min(occ.end, dayEnd) - Math.max(occ.at, midnight);
      if (covered < 6 * HOUR_MS) continue;
      extra = { noteDay: civilDate(occ.at, tz) };
    }
    if (kind === "tzolkin" || kind === "maya-sign") extra = { daySignIdx: maya.daySignIdx, todayTone };
    items.push({ kind, ...facts, ...occ, ...extra });
  }
  const kept = items.some((i) => i.kind === "tzolkin") ? items.filter((i) => i.kind !== "maya-sign") : items;
  kept.sort((a, b) => TODAY_ORDER.indexOf(a.kind) - TODAY_ORDER.indexOf(b.kind));
  return { today, timeZone: tz, items: kept };
}
